import { isReadOnly } from '@/lib/permissions'
import { type Subscription, useCancelSubscriptionMutation } from '@/services/subscriptions'
import { borderRadius, colors } from '@/theme'
import { CANCELLATION_REASONS, CANCELLATION_REASON_MAX_LENGTH } from '@/types/order'
import { zodResolver } from '@hookform/resolvers/zod'
import CloseIcon from '@mui/icons-material/Close'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { format } from 'date-fns'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

interface CancelSubscriptionDialogProps {
  open: boolean
  subscription: Subscription
  onClose: () => void
}

const cancelSchema = z.object({
  reason: z
    .string()
    .min(1, 'A cancellation reason is required')
    .max(CANCELLATION_REASON_MAX_LENGTH, `Must be ${CANCELLATION_REASON_MAX_LENGTH} characters or fewer`),
  cancelAtPeriodEnd: z.boolean()
})

type CancelFormData = z.infer<typeof cancelSchema>

function periodEndLabel(nextPaymentDate: string | null): string {
  if (!nextPaymentDate) return 'Cancel at the end of the current billing period'

  const date = new Date(nextPaymentDate.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return 'Cancel at the end of the current billing period'

  return `Cancel at the end of the current billing period (${format(date, 'dd/MM/yyyy')})`
}

export default function CancelSubscriptionDialog({ open, subscription, onClose }: CancelSubscriptionDialogProps) {
  const cancelMutation = useCancelSubscriptionMutation(subscription.hash)
  const isLoading = cancelMutation.isPending

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<CancelFormData>({
    resolver: zodResolver(cancelSchema),
    defaultValues: { reason: '', cancelAtPeriodEnd: false }
  })

  const handleClose = () => {
    if (isLoading) return
    reset()
    onClose()
  }

  const onSubmit = async (data: CancelFormData) => {
    if (isReadOnly()) return

    await cancelMutation.mutateAsync(data)
    reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: borderRadius.md } }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='sectionTitle'>Cancel subscription</Typography>
            <IconButton onClick={handleClose} size='small' disabled={isLoading}>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: 14, color: colors.text.primary, mb: 1.5 }}>
            Cancel <strong>{subscription.planName ?? 'this subscription'}</strong> for{' '}
            {subscription.user.displayName ?? subscription.user.email ?? 'this patient'}?
          </Typography>
          <Typography sx={{ fontSize: 14, color: colors.text.secondary, mb: 3 }}>
            Billing stops and the subscription cannot be reactivated. Medication orders already with the pharmacy carry
            on and need cancelling separately.
          </Typography>

          <Typography variant='fieldLabel' sx={{ display: 'block', mb: 1 }}>
            Reason for cancellation
          </Typography>
          <Controller
            name='reason'
            control={control}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                freeSolo
                value={value}
                onChange={(_event, newValue) => onChange(newValue ?? '')}
                onInputChange={(_event, newInputValue) => onChange(newInputValue)}
                options={[...CANCELLATION_REASONS]}
                disabled={isLoading}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder='Type or select reason'
                    error={!!errors.reason}
                    helperText={errors.reason?.message}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option} style={{ whiteSpace: 'normal' }}>
                    {option}
                  </li>
                )}
                slotProps={{ paper: { sx: { maxHeight: 300 } } }}
              />
            )}
          />

          <Controller
            name='cancelAtPeriodEnd'
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                sx={{ mt: 2 }}
                control={<Checkbox checked={value} onChange={event => onChange(event.target.checked)} />}
                disabled={isLoading}
                label={<Typography sx={{ fontSize: 14 }}>{periodEndLabel(subscription.nextPaymentDate)}</Typography>}
              />
            )}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant='ghost' onClick={handleClose} disabled={isLoading}>
            Keep subscription
          </Button>
          <Button type='submit' variant='primary' color='error' disabled={isLoading} loading={isLoading}>
            Cancel subscription
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
